/**
 * SurdurulebilirlikPage.jsx — sürdürülebilirlik beyanının tek ekranı
 * Route: /Management/Sustainability?tab=beyan|gonderilen
 *
 * 2026-09-12'ye kadar "Sürdürülebilirlik" adında ayrı bir üst menüydü, iki girdiye
 * dağılmıştı:
 *
 *   Beyan Formu Doldur   → /FdsSaha/Sustainability   (SustainabilityDeclarationForm)
 *   Gönderilen Beyanlar  → /Management/Sustainability (SustainabilitySubmissionsPage)
 *
 * Artık Form Yönetimi > Diğer Formlar altında tek sayfa, iki sekme. İki ekran da
 * `embedded` bayrağıyla kendi sayfa kabuğunu kapatır (bkz. Components/Common/
 * EmbeddablePage.jsx). Beyan formu kendi /FdsSaha rotasında durmaya devam ediyor —
 * firma kullanıcıları (grup 9/19) bu menüyü görmüyor, oraya doğrudan gidiyorlar.
 *
 * Yetki: gönderilen beyanlar ekranı yönetim işi (kendi eski rotası rol [1,2,3] idi),
 * bu yüzden o sekme yalnız yönetim gruplarına gösterilir.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container, Card, Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SustainabilityDeclarationForm from "../../FdsSaha/SustainabilityDeclarationForm";
import SustainabilitySubmissionsPage from "../Sustainability/SustainabilitySubmissionsPage";
// WAS (until #325): import { getCurrentPageAccessGroupId } from "../../../Routes/pageAccess";
// #325: the submissions tab opens with the form-design page permission, not role ids.
import { useYetki, SAYFA_IZNI } from "../../../services/rbacService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// WAS (until #325): const ADMIN_GROUP_IDS = [1, 2, 3];

// #302: tab access follows the role whose PAGES this login gets (a new role: its template's).
// WAS:
// function getCurrentGroupId() {
//   try {
//     const groupInfo = JSON.parse(localStorage.getItem("groupInfo"));
//     return groupInfo?.id ?? groupInfo?.userGroupEntity?.id ?? null;
//   } catch {
//     return null;
//   }
// }
// WAS (until #325):
// function getCurrentGroupId() {
//   return getCurrentPageAccessGroupId();
// }

const ALL_TABS = [
  { key: "beyan",      get label() { return i18n.t("formlar:surdurulebilirlik.beyanFormuDoldur"); }, icon: "bx-leaf",    adminOnly: false },
  { key: "gonderilen", get label() { return i18n.t("formlar:surdurulebilirlik.gonderilenBeyanlar"); }, icon: "bx-archive", adminOnly: true },
];

export default function SurdurulebilirlikPage() {
  const { t: tx } = useTranslation("formlar");
  const [searchParams, setSearchParams] = useSearchParams();

  // WAS (until #325): const isAdmin = ADMIN_GROUP_IDS.includes(getCurrentGroupId());
  const isAdmin = useYetki(SAYFA_IZNI.FORM_YONETIM) === true;
  const tabs    = ALL_TABS.filter((t) => isAdmin || !t.adminOnly);

  const tabParam  = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.key === tabParam) ? tabParam : tabs[0].key;

  // Açılmış sekme mount edilmiş kalır — yarım doldurulmuş beyan formu
  // sekme değiştirince kaybolmasın.
  const [visited, setVisited] = useState(() => ({ [activeTab]: true }));
  const isMounted = (key) => key === activeTab || visited[key];

  useEffect(() => { document.title = i18n.t("formlar:surdurulebilirlik.surdurulebilirlikFormuFds"); }, []);

  const setTab = (key) => {
    setVisited((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("surdurulebilirlik.surdurulebilirlikFormu")} pageTitle={tx("surdurulebilirlik.formYonetimi")} />

        <Card className="mb-0">
          <Nav tabs className="nav-tabs-custom border-bottom-0 px-3 pt-2">
            {tabs.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  className={`fw-semibold ${activeTab === t.key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                >
                  <i className={`bx ${t.icon} me-1 align-middle`} />
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Card>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="beyan">
            {isMounted("beyan") && <SustainabilityDeclarationForm embedded />}
          </TabPane>
          {isAdmin && (
            <TabPane tabId="gonderilen">
              {isMounted("gonderilen") && <SustainabilitySubmissionsPage embedded />}
            </TabPane>
          )}
        </TabContent>
      </Container>
    </div>
  );
}
